import { useAuth } from "#root/components/auth";
import { Dropdown } from "#root/components/dropdown";
import { useDropdown } from "#root/utility/hooks";
import { Fragment, type ReactNode } from "react";
import { MdOutlineCloud } from "react-icons/md";
import { Link, NavLink } from "react-router-dom";
import { Logo } from "../components/logo";

export type PageLayoutProps = {
  children?: ReactNode;
  title?: string | null | undefined;
  description?: string | null | undefined;
  secondaryNavElements?: ReactNode;
};

export default function PageLayout({ children, title, description = "", secondaryNavElements = undefined }: PageLayoutProps): ReactNode {
  if (title) {
    document.title = title;
  }

  document.querySelector("#metaDescription")?.setAttribute("content", description ?? "");
  return (
    <Fragment>
      <Nav/>
      <nav className="secondary">{secondaryNavElements}</nav>
      {children}
      <Footer/>
    </Fragment>
  );
}

function Nav() {
  const [dropdown, toggleDropdown] = useDropdown();
  const auth = useAuth();

  return (
    <nav className="primary">
      <Link to="/" className="logo">
        <MdOutlineCloud/>
      </Link>
      <NavLink to="/products/api">API Directory</NavLink>
      <NavLink to="/docs">Documentation</NavLink>
      <div className="spacer"/>
      {auth?.authInfo?.isSuperAdmin ? (
        <Fragment>
          <Dropdown id="billing" label="Billing" open={dropdown === "billing"} onToggle={toggleDropdown}>
            <Link to="/billing/invoice">Invoices</Link>
            <Link to="/billing/correspondence">Correspondence</Link>
            <Link to="/billing/usage">API Usage</Link>
          </Dropdown>
          <Dropdown id="admin" label="Admin" open={dropdown === "admin"} onToggle={toggleDropdown}>
            <Link to="/admin/account">Accounts</Link>
            <Link to="/admin/article">Articles</Link>
            <Link to="/admin/platform">Platform</Link>
            <Link to="/admin/product">Products</Link>
            <Link to="/admin/user">Users</Link>
            <Link to="/admin/vendor">Vendor</Link>
          </Dropdown>
        </Fragment>
      ) : null}
      {auth?.authInfo?.id ? (
        <Fragment>
          <Dropdown id="account" label="My Account" open={dropdown === "account"} onToggle={toggleDropdown}>
            <Link to="/account/password">Change Password</Link>
            <Link to="/account/deployment">Deployments</Link>
            <Link to="/account/invoice">Invoices</Link>
            <Link to="/account/profile">My Profile</Link>
            <Link to="/account/syndicatedContent">Syndicated Content</Link>
          </Dropdown>
          <Link to="/logout">Logout</Link>
          <Link to="/contact">Contact Us</Link>
        </Fragment>
      ) : (
        <Fragment>
          <Link to="/login">Log In</Link>
          <Link to="/signup">Sign Up</Link>
          <Link to="/contact">Contact Us</Link>
        </Fragment>
      )}
    </nav>
  );
}

function Footer() {
  return <footer className="bg-[#041c30] text-[#94a3b8]">
    <div className="max-w-240 mx-auto py-8 px-8">
      <div className="grid grid-cols-1 md:grid-cols-[repeat(3,auto)] gap-16 justify-between">
        <section>
          <div className="mb-8 ">
            <Logo/>
          </div>
          <ul className="space-y-4 m-0 p-0 list-none">
            <li className="flex items-start">
              <a
                className="text-[#cbd5e1] hover:text-white transition-colors"
                href="https://www.google.com/maps/place/FinancialContent/@37.5141456,-122.2639589,15z/data=!4m2!3m1!1s0x0:0xaff3350de005c778?sa=X&amp;ved=2ahUKEwiz45CC79_iAhUZHjQIHZZ2AIIQ_BIwDXoECAgQCA"
                target="_blank"
                rel="noopener"
              >
                195 Glenn Way Suite 250
                <br/>
                San Carlos, CA 94070
              </a>
            </li>
            <li>
              <a className="text-[#cbd5e1] hover:text-white transition-colors" href="tel:888-688-9880">(888) 688-9880</a>
            </li>
          </ul>
        </section>
        <section>
          <h4 className="text-white font-bold text-sm uppercase tracking-[0.1em] mb-8">Learn More</h4>
          <ul className="space-y-4 m-0 p-0 list-none text-sm">
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/docs">Documentation</a>
            </li>
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/docs/quickstart">Quick Start</a>
            </li>
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/products/api">API Directory</a>
            </li>
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/docs/data-coverage">Data Coverage</a>
            </li>
          </ul>
        </section>
        <section>
          <h4 className="text-white font-bold text-sm uppercase tracking-[0.1em] mb-8">FinancialContent</h4>
          <ul className="space-y-4 m-0 p-0 list-none text-sm">
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/article/about-us">About Us</a>
            </li>
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/contact">Contact Us</a>
            </li>
            <li>
              <a className="text-[#94a3b8] hover:text-white transition-colors text-base" href="/article/jobs">Jobs</a>
            </li>
          </ul>
        </section>
      </div>
      <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 text-md">
        <p className="m-0 text-slate-500">
          © {new Date().getFullYear()} FinancialContent Services, Inc.
        </p>
        <div className="flex space-x-8 ">
          <a className="text-slate-500 hover:text-white transition-colors" href="/article/5">Terms</a>
          <a className="text-slate-500 hover:text-white transition-colors" href="/article/6">Privacy</a>
        </div>
      </div>
    </div>
  </footer>;
}
